import { StandardEvents } from '@shopify/events';

/**
 * Keeps the product quantity selector within the stock available for the
 * selected Shopify variant. It is loaded only by the private BedaPeques
 * product template.
 */
class BedaPequesStockControl extends HTMLElement {
  #eventTarget = null;

  connectedCallback() {
    this.#eventTarget = this.closest('[id*="ProductInformation-"], dialog, .shopify-section');
    this.#eventTarget?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);

    customElements.whenDefined('quantity-selector-component').then(() => {
      window.requestAnimationFrame(() => this.#applyLimit());
    });
  }

  disconnectedCallback() {
    this.#eventTarget?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const incoming = html.querySelector('bp-stock-control');
        if (!incoming) return;

        this.dataset.stock = incoming.dataset.stock ?? '';
        this.dataset.managed = incoming.dataset.managed ?? 'false';
        this.dataset.policy = incoming.dataset.policy ?? '';

        window.requestAnimationFrame(() => this.#applyLimit());
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[bp-stock-control] Event promise rejected:', error);
        }
      });
  };

  #applyLimit() {
    const root = this.closest('[id*="ProductInformation-"], dialog, .shopify-section');
    const quantitySelector =
      root?.querySelector('product-form-component quantity-selector-component') ??
      root?.querySelector('quantity-selector-component');

    const input = quantitySelector?.querySelector('input[ref="quantityInput"]');
    if (!quantitySelector || !input) return;

    const stock = Number.parseInt(this.dataset.stock ?? '', 10);
    const mustLimit =
      this.dataset.managed === 'true' &&
      this.dataset.policy === 'deny' &&
      Number.isFinite(stock);

    if (mustLimit) {
      const safeStock = Math.max(0, stock);
      input.max = String(safeStock);
      quantitySelector.updateConstraints?.(
        input.min || '1',
        String(safeStock),
        input.step || '1'
      );
      return;
    }

    input.removeAttribute('max');
    quantitySelector.updateConstraints?.(input.min || '1', null, input.step || '1');
  }
}

if (!customElements.get('bp-stock-control')) {
  customElements.define('bp-stock-control', BedaPequesStockControl);
}
